/*
 * The plan sheet (design W3): pick a plan, then one checkout button, one
 * Stripe Checkout session per click. Opens on `?plans=open` from the
 * free-event banner, the limited dashboard and the restore screen. Prices
 * render only from GET /api/v3/plans (Req 11); none is written here.
 */
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { XMarkIcon, ShieldCheckIcon, CheckIcon, InformationCircleIcon } from "@heroicons/react/24/solid";
import { ft, Btn, Alert } from "components/FreeEvent";
import { selectUser, selectAuthToken } from "containers/App/selectors";
import { useBillingPortalSession } from "containers/App/api/mutations";
import { freeEventPhase } from "containers/FreeEventBanner/state";
import { getPlans, postCheckoutSession } from "utils/api/freeStart";
import { MARKETING_RENT_URL } from "utils/marketing";
import { trackCheckoutSheetOpened, trackCheckoutStarted } from "utils/freeEventAnalytics";
import "containers/FreeStart/free-start.css";

const PLANS = ["lite", "plus", "pro"];
const INTERVALS = ["month", "year"];
const FOCUSABLE = "a[href], button, input, select, textarea, [tabindex]";
const selectUserState = selectUser();
const selectToken = selectAuthToken();

export const PlanSheetView = ({ token, fe, onClose }) => {
  const [interval, setIv] = useState("month");
  const [plan, setPlan] = useState("pro");
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const sheetRef = useRef(null);
  const portal = useBillingPortalSession();
  const prices = useQuery(["freeStart", "plans"], () => getPlans(token), { staleTime: 5 * 60000, retry: 1 });
  const phase = freeEventPhase(fe);
  const have = problem === "have";

  useEffect(() => {
    trackCheckoutSheetOpened();
    const prev = document.activeElement;
    if (sheetRef.current) sheetRef.current.focus();
    // Escape closes; Tab and Shift+Tab stay inside the sheet (aria-modal).
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key !== "Tab" || !sheetRef.current) return;
      const items = Array.from(sheetRef.current.querySelectorAll(FOCUSABLE)).filter(
        (el) => !el.disabled && el.getAttribute("tabindex") !== "-1"
      );
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const inside = sheetRef.current.contains(document.activeElement);
      if (e.shiftKey && (!inside || document.activeElement === first || document.activeElement === sheetRef.current)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (!inside || document.activeElement === last)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      if (prev && prev.focus) prev.focus();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkout = async () => {
    if (busy || have) return;
    setBusy(true);
    setProblem(null);
    try {
      trackCheckoutStarted(plan, interval);
      const res = await postCheckoutSession(token, { plan, interval });
      window.location.assign(res.url);
    } catch (err) {
      setProblem(err && err.statusCode === 409 ? "have" : "error");
      setBusy(false);
    }
  };

  const manage = async () => {
    try {
      const res = await portal.mutateAsync({});
      if (res && res.url) window.location.assign(res.url);
    } catch (_e) {
      setProblem("error");
    }
  };

  const priceOf = (p) => {
    if (prices.isLoading) return <span className="skel" style={{ display: "block", width: "60%", height: 24 }} />;
    const price = prices.data && prices.data[p] && prices.data[p][interval];
    // 503 prices_unavailable (Stripe unreadable, nothing cached): checkout
    // still works and Stripe shows the price, so say where to find it.
    if (!price) return <span className="pr-none">{ft("pl.price.none")}</span>;
    return (
      <span className="pr">
        {price}
        <small> {ft(interval === "year" ? "pl.per.y" : "pl.per")}</small>
      </span>
    );
  };

  // Arrow keys move the choice inside a radio group, as a native one does.
  const arrows = (list, value, set) => (e) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    if (!step) return;
    e.preventDefault();
    const next = list[(list.indexOf(value) + step + list.length) % list.length];
    set(next);
    const el = e.currentTarget.querySelector(`[data-v="${next}"]`);
    if (el) el.focus();
  };

  return (
    <div className="mb-fs ovl">
      <div className="scrim" onClick={onClose} aria-hidden="true" />
      <div className="sheet" role="dialog" aria-modal="true" aria-labelledby="pl-h" tabIndex={-1} ref={sheetRef}>
        <button type="button" className="x" aria-label={ft("pl.close")} onClick={onClose}>
          <XMarkIcon className="ic" aria-hidden="true" />
        </button>
        <h2 id="pl-h">{ft("pl.h")}</h2>
        {(phase === "retained" || phase === "restorable") && <p className="sub">{ft(`pl.sub_${phase}`)}</p>}
        {problem === "error" && <Alert kind="err" title={ft("pl.err")} />}
        {have && (
          <Alert kind="info" icon={InformationCircleIcon} title={ft("pl.have.b")}>
            {ft("pl.have")}
            <p style={{ marginTop: 12 }}>
              <Btn className="sm" busy={portal.isLoading} onClick={manage}>
                {ft("pl.manage")}
              </Btn>
            </p>
          </Alert>
        )}
        <div className="seg" role="radiogroup" aria-label={ft("pl.h")} onKeyDown={arrows(INTERVALS, interval, setIv)}>
          {INTERVALS.map((iv) => (
            <button
              key={iv}
              type="button"
              role="radio"
              data-v={iv}
              aria-checked={interval === iv}
              tabIndex={interval === iv ? 0 : -1}
              onClick={() => setIv(iv)}
            >
              {ft(iv === "year" ? "pl.y" : "pl.m")}
            </button>
          ))}
        </div>
        <div
          className={`plans${have ? " off" : ""}`}
          role="radiogroup"
          aria-labelledby="pl-h"
          aria-disabled={have || undefined}
          onKeyDown={have ? undefined : arrows(PLANS, plan, setPlan)}
        >
          {PLANS.map((p) => (
            <div
              key={p}
              className="plan"
              role="radio"
              data-v={p}
              aria-checked={plan === p}
              aria-labelledby={`pl-n-${p}`}
              aria-describedby={`pl-d-${p}`}
              aria-disabled={have || undefined}
              tabIndex={plan === p ? 0 : -1}
              onClick={() => !have && setPlan(p)}
            >
              <div className="nm">
                <span id={`pl-n-${p}`}>{ft(`pl.name.${p}`)}</span>
                <span className="rd">{plan === p && <CheckIcon className="ic" aria-hidden="true" />}</span>
              </div>
              {p === "pro" && phase && <span className="flag">{ft(phase === "upcoming" ? "pl.flag_upcoming" : "pl.flag_after")}</span>}
              {priceOf(p)}
              <p className="bl" id={`pl-d-${p}`}>
                {ft(`pl.${p}`)}
              </p>
            </div>
          ))}
        </div>
        <div className="sfoot">
          <div>
            <p className="gtee">
              <ShieldCheckIcon className="ic" aria-hidden="true" />
              <span>{ft("pl.gtee")}</span>
            </p>
            <p className="rent">
              {ft("pl.rent.q")} <a href={MARKETING_RENT_URL}>{ft("pl.rent")}</a>
            </p>
          </div>
          <Btn busy={busy} busyLabel={ft("pl.busy")} disabled={have} onClick={checkout}>
            {ft("pl.btn")}
          </Btn>
        </div>
      </div>
    </div>
  );
};

const PlanSheet = () => {
  const [params, setParams] = useSearchParams();
  const user = useSelector(selectUserState);
  const token = useSelector(selectToken);
  if (params.get("plans") !== "open" || !token) return null;
  const close = () =>
    setParams(
      (p) => {
        p.delete("plans");
        return p;
      },
      { replace: true }
    );
  return <PlanSheetView token={token} fe={user && user.free_event} onClose={close} />;
};

export default PlanSheet;
